import { useEffect } from 'react';
import { matchesShortcut, shouldSkipShortcut } from '@/utils/input';
import { formatDateTime as canonicalFormatDateTime } from '@/utils/dateTime';

export type DesignerTab = 'designer' | 'monitoring';

/**
 * Formats a "how long since the last refresh" label for the monitoring
 * refresh chip. Pure — takes `now` and `updatedAt` as inputs so the
 * component owning the refresh clock can pass them in without leaking
 * `Date.now()` into the formatter.
 *
 * Returns an empty string when `updatedAt` is falsy (never refreshed).
 */
export function formatRelativeRefreshLabel(now: number, updatedAt: number | null | undefined): string {
  if (!updatedAt) return '';
  const seconds = Math.max(0, Math.floor((now - updatedAt) / 1000));
  if (seconds < 5) return 'Updated just now';
  if (seconds < 60) return `Updated ${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `Updated ${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Updated ${hours}h ago`;
  return `Updated ${canonicalFormatDateTime(updatedAt, 'locale', { fallback: '' })}`;
}

/**
 * Registers a document keydown listener that switches between the
 * designer and monitoring tabs. Respects `shouldSkipShortcut` (input
 * focus, IME composition) and de-duplicates key repeat events so
 * holding a key doesn't fire the handler multiple times.
 */
export function useTabKeyboardShortcuts(
  handleTabChange: (tab: DesignerTab) => void
): void {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (shouldSkipShortcut(e)) return;

      if (matchesShortcut(e, 'switchToDesigner')) {
        e.preventDefault();
        if (e.repeat) return;
        handleTabChange('designer');
      } else if (matchesShortcut(e, 'switchToMonitoring')) {
        e.preventDefault();
        if (e.repeat) return;
        handleTabChange('monitoring');
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleTabChange]);
}
